import { Observable } from 'rxjs';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { SKIP_ERROR_PAGE } from 'src/app/error/error.interceptor';
import {
  StoreCatalog,
  SaveOfferPositionView,
  SaveCategoryPositionView,
  ContentItemImageTarget,
  ContentItemView,
  SaveOfferView,
  SaveCategoryView,
  CategoriesListResponse,
  Availability,
  UploadFileResponse,
  AssociateCategoriesView,
  AssociateOffersView,
  TranslateCatalogXlsResponse,
  ToUpdateCatalogXlsResponse,
  PatchSaveOfferView,
  ImportCatalogResponse,
  OfferImageResponse,
  CloneCatalogRequest,
  Category,
  Station,
  SaveStationRequest,
  SaveStationResponse,
  SavePrinterRequest,
  SavePrinterResponse,
  OverrideOfferView,
  Offer,
  ItemTag,
  ItemTagRequest,
  ItemTagPosition,
} from './stores-catalog';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  CatalogImport,
  CatalogImportType,
} from './+state/stores-catalog.reducer';

@Injectable({
  providedIn: 'root',
})
export class StoresCatalogService {
  constructor(private http: HttpClient) {}

  importCatalogFromOtherStore(
    cloneRequest: CloneCatalogRequest,
  ): Observable<any> {
    return this.http.post(`/api/v1/stores/cloneCatalog`, cloneRequest);
  }

  getcatalog(id: string): Observable<StoreCatalog> {
    return this.http.get<StoreCatalog>(`/api/v1/user/stores/${id}/catalog`);
  }

  getStations(id: string): Observable<Station[]> {
    return this.http.get<Station[]>(`/api/v1/stores/${id}/stations`);
  }

  getPrinters(id: string): Observable<SavePrinterResponse[]> {
    return this.http.get<SavePrinterResponse[]>(
      `/api/v1/admin/stores/${id}/printers`,
    );
  }

  getPrinter(
    storeId: number,
    printerId: number,
  ): Observable<SavePrinterResponse> {
    return this.http.get<SavePrinterResponse>(
      `/api/v1/admin/stores/${storeId}/printers/${printerId}`,
    );
  }

  savePrinter(
    savePrinterRequest: SavePrinterRequest,
    storeId: number,
  ): Observable<SavePrinterResponse> {
    return this.http.post<SavePrinterResponse>(
      `/api/v1/admin/stores/${storeId}/printers`,
      savePrinterRequest,
    );
  }

  updatePrinter(
    savePrinterRequest: SavePrinterRequest,
    storeId: number,
    printerId: number,
  ): Observable<SavePrinterResponse> {
    return this.http.put<SavePrinterResponse>(
      `/api/v1/admin/stores/${storeId}/printers/${printerId}`,
      savePrinterRequest,
    );
  }

  deletePrinter(storeId: number, printerId: number): Observable<void> {
    return this.http.delete<void>(
      `/api/v1/admin/stores/${storeId}/printers/${printerId}`,
    );
  }

  saveOffer(
    offer: SaveOfferView,
    offerId: any,
    storeId: number,
    catalogId: number,
  ): Observable<SaveOfferView> {
    if (
      offerId === 'CREATE_OFFER' ||
      offerId === 'CREATE_GIFT_CARD' ||
      offerId === 'CREATE_COMBO'
    ) {
      return this.http.post<SaveOfferView>(
        `/api/v2/stores/${storeId}/catalog/${catalogId}/offer`,
        offer,
      );
    } else {
      return this.http.post<SaveOfferView>(
        `/api/v2/stores/${storeId}/catalog/${catalogId}/offer/${offerId}`,
        offer,
      );
    }
  }

  patchOffer(
    offer: PatchSaveOfferView,
    offerId: any,
    storeId: number,
    catalogId: number,
  ): Observable<PatchSaveOfferView> {
    return this.http.patch<PatchSaveOfferView>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/${offerId}/lite`,
      offer,
    );
  }

  overrideOffer(
    offer: OverrideOfferView,
    storeId: number,
    catalogId: number,
  ): Observable<Offer> {
    return this.http.post<Offer>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/override`,
      offer,
    );
  }

  bulkOfferChangeStatus(
    offerIds: number[],
    isOrderable: boolean,
    sellable: boolean,
    isStockCheckEnabled: boolean,
    stockLevel: number,
    storeId: number,
    catalogId: number,
    itemType?: string,
  ): Observable<PatchSaveOfferView[]> {
    const request = {
      offerIds,
      isOrderable,
      sellable,
      isStockCheckEnabled,
      stockLevel,
      itemType,
    };
    return this.http.patch<PatchSaveOfferView[]>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/changeStatus`,
      request,
    );
  }

  saveImageToOfferOrCategory(
    contentItemId: number,
    imageUrl: any,
    storeId: number,
    target: ContentItemImageTarget = 'standardImage',
  ): Observable<ContentItemView> {
    // Only the targeted field is sent; the backend leaves an absent field untouched. An empty
    // variantIcon is never sent from here: the icon is removed through deleteOfferImage (#11754).
    const contentItem: ContentItemView =
      target === 'variantIcon'
        ? { variantIcon: imageUrl }
        : { standardImage: imageUrl };
    return this.saveContentItem(contentItem, contentItemId, storeId);
  }

  /**
   * Removes the item image, or the variant icon (#11754) when `target` is `'variantIcon'`.
   * Sends `DELETE …/delete-image` (photo) or `?imageType=VARIANT_ICON` (icon) — pairs with
   * stores-backend #6159 `OfferImageType` / `OfferService.deleteOfferImage`.
   */
  deleteOfferImage(
    storeId: number,
    catalogId: number,
    offerId: number,
    target: ContentItemImageTarget = 'standardImage',
  ): Observable<void> {
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/${offerId}/delete-image`,
      target === 'variantIcon'
        ? { params: { imageType: 'VARIANT_ICON' } }
        : undefined,
    );
  }

  deleteCategoryImage(
    storeId: number,
    catalogId: number,
    categoryId: number,
  ): Observable<void> {
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/category/${categoryId}/image`,
    );
  }

  saveOfferPosition(
    offers: SaveOfferPositionView,
    storeId,
    catalogId,
  ): Observable<SaveOfferPositionView> {
    return this.http.post<SaveOfferPositionView>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/position`,
      offers,
    );
  }

  saveCategoryPosition(
    categories: SaveCategoryPositionView,
    storeId,
    catalogId,
  ): Observable<SaveCategoryPositionView> {
    return this.http.post<SaveCategoryPositionView>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/category/position`,
      categories,
    );
  }

  loadOffer(
    offerId: string,
    storeId: string,
    catalogId: string,
  ): Observable<SaveOfferView> {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    });

    return this.http.get<SaveOfferView>(
      `/api/v2/user/stores/${storeId}/catalog/${catalogId}/offer/${offerId}`,
      { headers },
    );
  }

  saveCategory(
    category: SaveCategoryView,
    categoryId: any,
    storeId: number,
    catalogId: number,
  ): Observable<SaveCategoryView> {
    if (categoryId === 'CREATE_CATEGORY') {
      return this.http.post<SaveCategoryView>(
        `/api/v1/stores/${storeId}/catalog/${catalogId}/category`,
        category,
      );
    } else {
      return this.http.post<SaveCategoryView>(
        `/api/v1/stores/${storeId}/catalog/${catalogId}/category/${categoryId}`,
        category,
      );
    }
  }

  saveStation(
    saveStationRequest: SaveStationRequest,
    storeId: number,
  ): Observable<SaveStationResponse> {
    return this.http.post<SaveStationResponse>(
      `/api/v1/stores/${storeId}/stations`,
      saveStationRequest,
    );
  }

  updateStation(
    saveStationRequest: SaveStationRequest,
    storeId: number,
    stationId: number,
  ): Observable<SaveStationResponse> {
    return this.http.patch<SaveStationResponse>(
      `/api/v1/stores/${storeId}/stations/${stationId}`,
      saveStationRequest,
    );
  }

  deleteStation(storeId: number, stationId: number): Observable<void> {
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/stations/${stationId}`,
    );
  }

  loadCategory(
    categoryId: string,
    storeId: string,
    catalogId: string,
  ): Observable<SaveCategoryView> {
    return this.http.get<SaveCategoryView>(
      `/api/v1/user/stores/${storeId}/catalog/${catalogId}/category/${categoryId}`,
    );
  }

  loadStationCategory(
    stationId: number,
    storeId: number,
  ): Observable<SaveStationResponse> {
    return this.http.get<SaveStationResponse>(
      `/api/v1/stores/${storeId}/stations/${stationId}`,
    );
  }

  listCatalogCategories(
    catalogId: number,
    storeId: number,
  ): Observable<Category[]> {
    return this.http.get<Category[]>(
      `/api/v1/user/stores/${storeId}/catalog/${catalogId}/categories/name`,
    );
  }

  loadCategoriesByLevel(
    catalogId: number,
    storeId: number,
    level: string,
  ): Observable<Category[]> {
    return this.http.get<Category[]>(
      `/api/v1/user/stores/${storeId}/catalog/${catalogId}/categories/name?level=${level}`,
    );
  }

  loadCategoriesByLevelPaginated(
    catalogId: number,
    storeId: number,
    level: string,
    languageId?: string,
    page?: number,
    size?: number,
    sort?: string,
    search?: string,
  ): Observable<any> {
    let url = `/api/v1/user/stores/${storeId}/catalog/${catalogId}/categories?level=${level}`;

    if (languageId) {
      url += `&languageId=${languageId}`;
    }
    if (page !== undefined) {
      url += `&page=${page}`;
    }
    if (size !== undefined) {
      url += `&size=${size}`;
    }
    if (sort) {
      url += `&sort=${sort}`;
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return this.http.get<any>(url);
  }

  loadOffersByLevel(
    catalogId: number,
    storeId: number,
    level: string,
    languageId?: string,
    page?: number,
    size?: number,
    sort?: string,
    search?: string,
  ): Observable<any> {
    let url = `/api/v1/user/stores/${storeId}/catalog/${catalogId}/offers?level=${level}`;

    if (languageId) {
      url += `&languageId=${languageId}`;
    }
    if (page !== undefined) {
      url += `&page=${page}`;
    }
    if (size !== undefined) {
      url += `&size=${size}`;
    }
    if (sort) {
      url += `&sort=${sort}`;
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return this.http.get<any>(url);
  }

  listAvailability(storeId: number): Observable<Availability> {
    return this.http.get<Availability>(
      `/api/v1/stores/${storeId}/availability`,
    );
  }

  saveAvailability(
    availability: Availability,
    storeId: number,
  ): Observable<Availability> {
    return this.http.post<Availability>(
      `/api/v1/stores/${storeId}/availability`,
      availability,
    );
  }

  uploadOfferOrCategoryImage(
    file,
    storeId,
    contentItemId,
    target: ContentItemImageTarget = 'standardImage',
  ): Observable<UploadFileResponse> {
    const formdata = new FormData();
    formdata.append('file', file);
    // The variant icon has its own upload folder, so it never overwrites the item photo.
    const fileType =
      target === 'variantIcon'
        ? 'CONTENT_ITEM_VARIANT_ICON'
        : 'CONTENT_ITEM_PICTURE';
    return this.http.post<UploadFileResponse>(
      `/api/v1/files/stores/${storeId}/content-items/${contentItemId}/${fileType}`,
      formdata,
    );
  }
  uploadCatalogTranslate(
    file: any,
    storeId: number,
    catId: number,
  ): Observable<CatalogImport> {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post<CatalogImport>(
      `/api/v1/stores/${storeId}/catalog/${catId}/content`,
      formdata,
    );
  }

  getItemTags(storeId: number, catalogId: number): Observable<ItemTag[]> {
    // The item page and the Tags list handle a failure themselves (#11750).
    return this.http.get<ItemTag[]>(this.itemTagsUrl(storeId, catalogId), {
      context: new HttpContext().set(SKIP_ERROR_PAGE, true),
    });
  }

  createItemTag(
    storeId: number,
    catalogId: number,
    request: ItemTagRequest,
  ): Observable<ItemTag> {
    // The Add Tag dialog stays open and shows the error itself (#11750).
    return this.http.post<ItemTag>(
      this.itemTagsUrl(storeId, catalogId),
      request,
      {
        context: new HttpContext().set(SKIP_ERROR_PAGE, true),
      },
    );
  }

  updateItemTag(
    storeId: number,
    catalogId: number,
    tagId: number,
    request: ItemTagRequest,
  ): Observable<ItemTag> {
    return this.http.patch<ItemTag>(
      `${this.itemTagsUrl(storeId, catalogId)}/${tagId}`,
      request,
    );
  }

  deleteItemTag(
    storeId: number,
    catalogId: number,
    tagId: number,
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.itemTagsUrl(storeId, catalogId)}/${tagId}`,
    );
  }

  saveItemTagPositions(
    storeId: number,
    catalogId: number,
    positions: ItemTagPosition[],
  ): Observable<ItemTag[]> {
    return this.http.post<ItemTag[]>(
      `${this.itemTagsUrl(storeId, catalogId)}/position`,
      positions,
    );
  }

  private itemTagsUrl(storeId: number, catalogId: number): string {
    return `/api/v1/stores/${storeId}/catalog/${catalogId}/item-tags`;
  }

  saveContentItem(
    contentItem: ContentItemView,
    contentItemId: number,
    storeId: number,
  ): Observable<ContentItemView> {
    return this.http.post<ContentItemView>(
      `/api/v1/stores/${storeId}/content-item/${contentItemId}`,
      contentItem,
    );
  }

  associateChildCategories(
    categories,
    offerId,
    storeId,
    catalogId,
  ): Observable<AssociateCategoriesView> {
    return this.http.post<AssociateCategoriesView>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/${offerId}/associate`,
      categories,
    );
  }

  associateChildOffers(
    offers,
    categoryId,
    storeId,
    catalogId,
  ): Observable<AssociateOffersView> {
    return this.http.post<AssociateOffersView>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/category/${categoryId}/associate`,
      offers,
    );
  }

  addChildOffer(
    storeId,
    catalogId,
    offer,
    childCategoryId,
    category: SaveCategoryView,
  ) {
    if (
      null != category &&
      category.categoryId === childCategoryId &&
      offer.hierarchyLevel === 'CHILD'
    ) {
      const offerList = [];
      if (null != category.offers) {
        category.offers.forEach((off) => {
          offerList.push({ offerId: off.offerId });
        });
      }
      offerList.push({ offerId: offer.offerId });
      const offers = { offers: offerList };
      this.associateChildOffers(
        offers,
        childCategoryId,
        storeId,
        catalogId,
      ).subscribe();
    }
  }

  associateOffersVariants(
    offers,
    offerId,
    storeId,
    catalogId,
  ): Observable<AssociateOffersView> {
    return this.http.post<AssociateOffersView>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/${offerId}/variant/associate`,
      offers,
    );
  }

  getPendingCatalogImport(
    storeId: string | number,
    catalogId: string | number,
    catalogImportId: string | number,
    catalogImportType: CatalogImportType,
  ): Observable<CatalogImport> {
    return this.http.get<CatalogImport>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/import-file/${catalogImportId}?catalogImportType=${catalogImportType}`,
    );
  }

  removePendingCatalogImport(
    storeId: string | number,
    catalogId: string | number,
    catalogImportId,
  ): Observable<any> {
    return this.http.delete<any>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/import-file/${catalogImportId}`,
    );
  }

  verifyCatalogData(
    file,
    storeId,
    catalogId,
    isOptionGroup,
  ): Observable<StoreCatalog> {
    const formdata = new FormData();
    formdata.append('file', file);
    if (isOptionGroup) {
      return this.http.post<StoreCatalog>(
        `/api/v1/stores/${storeId}/catalog/${catalogId}/verify/optionGroup`,
        formdata,
      );
    }
    return this.http.post<StoreCatalog>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/verify`,
      formdata,
    );
  }

  verifyCatalogContentData(file, storeId, catalogId): Observable<StoreCatalog> {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post<StoreCatalog>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/content`,
      formdata,
    );
  }

  verifyCatalogFile(
    file: File,
    storeId: number,
    catalogId: number,
    catalogImportType: CatalogImportType,
  ): Observable<CatalogImport> {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post<CatalogImport>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/import-file?catalogImportType=${catalogImportType}`,
      formdata,
    );
  }

  UploadCatalogData(
    catalog,
    storeId,
    catalogId,
    isOptionGroup,
    catalogImport: CatalogImport,
  ): Observable<CatalogImport> {
    if (catalogImport) {
      return this.http.post<CatalogImport>(
        `/api/v1/stores/${storeId}/catalog/${catalogId}/import-file/${catalogImport.id}/process?isOptionGroup=${isOptionGroup}`,
        catalog,
      );
    }
    if (isOptionGroup) {
      return this.http.post<CatalogImport>(
        `/api/v1/stores/${storeId}/catalog/${catalogId}/uploadOptionGroup`,
        catalog,
      );
    }
    return this.http.post<CatalogImport>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}`,
      catalog,
    );
  }

  ImportCatalog(
    externalDomain,
    externalReference,
    storeId,
  ): Observable<ImportCatalogResponse> {
    return this.http.post<ImportCatalogResponse>(
      `/api/v1/stores/${storeId}/import/scrapping-store`,
      { externalDomain, externalReference },
    );
  }

  addChildCategory(
    saveCategoryData: SaveCategoryView,
    offer: SaveOfferView,
    parentOfferId,
    storeId,
    catalogId,
  ) {
    if (
      null != offer &&
      offer.offerId === parentOfferId &&
      saveCategoryData.hierarchyLevel === 'CHILD' &&
      offer.hierarchyLevel === 'PARENT'
    ) {
      const catList = [];
      if (null != offer.categories) {
        offer.categories.forEach((cat) => {
          catList.push({ categoryId: cat.categoryId });
        });
      }
      catList.push({ categoryId: saveCategoryData.categoryId });
      const categories = { categories: catList };
      this.associateChildCategories(
        categories,
        parentOfferId,
        storeId,
        catalogId,
      ).subscribe();
    }
  }

  downloadOfferImage(url: string): Observable<OfferImageResponse> {
    return this.http
      .get(url, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toOfferImageResponse(r)));
  }

  private toOfferImageResponse(
    response: HttpResponse<Blob>,
  ): OfferImageResponse {
    const blob = response.body;
    const filename = response.url.split('/').pop().split('?')[0];
    return { blob, filename };
  }

  deleteCatalog(storeId, cleanOtherObjects): Observable<void> {
    const request = {
      storeId,
      cleanOtherObjects,
    };
    return this.http.delete<void>(`/api/v1/stores/catalog`, {
      body: request,
    });
  }

  deleteCategory(storeId, catalogId, categoryId): Observable<void> {
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/category/${categoryId}`,
    );
  }

  deleteOffer(storeId, catalogId, offerId): Observable<void> {
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/${offerId}`,
    );
  }

  deleteMultipleOffer(storeId, catalogId, offerIds): Observable<void> {
    const request = {
      body: { offerIds },
    };
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/offer/deletedMultipleOffers`,
      request,
    );
  }

  downloadTranslateCatalogXls(
    storeId: number,
    catalogId: number,
  ): Observable<TranslateCatalogXlsResponse> {
    return this.http
      .get(`/api/v1/stores/${storeId}/catalog/${catalogId}/content`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toTranslateCatalogXls(r)));
  }

  downloadToUpdateCatalogXls(
    storeId: number,
    catalogId: number,
    sourcePage: string,
  ): Observable<ToUpdateCatalogXlsResponse> {
    let api = `/api/v1/stores/${storeId}/catalog/${catalogId}/content/primary`;
    if (sourcePage && sourcePage === 'option-group') {
      api = `/api/v1/stores/${storeId}/catalog/${catalogId}/content/optionGroup`;
    }
    return this.http
      .get(api, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toTranslateCatalogXls(r)));
  }

  private toTranslateCatalogXls(
    response: HttpResponse<Blob>,
  ): TranslateCatalogXlsResponse {
    const blob = response.body;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
      .slice(contentDisposition.indexOf('filename=') + 9)
      .replace(/"/g, '');
    return { blob, filename };
  }

  // Product Import/Export Methods
  downloadProductTemplate(
    storeId: number,
    catalogId: number,
  ): Observable<ToUpdateCatalogXlsResponse> {
    return this.http
      .get(
        `/api/v1/stores/${storeId}/catalog/${catalogId}/content/product/template`,
        {
          observe: 'response',
          responseType: 'blob',
        },
      )
      .pipe(map((r) => this.toTranslateCatalogXls(r)));
  }

  downloadProductExcel(
    storeId: number,
    catalogId: number,
  ): Observable<ToUpdateCatalogXlsResponse> {
    return this.http
      .get(`/api/v1/stores/${storeId}/catalog/${catalogId}/content/product`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toTranslateCatalogXls(r)));
  }

  /**
   * Verify product import file
   * Uploads and validates a product import file
   * @param file - The Excel file to upload
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param catalogImportType - The import type (PRODUCT_EXCEL or PRODUCT_UPDATE_EXCEL)
   * @returns Observable of CatalogImport with validation results
   */
  verifyProductFile(
    file: File,
    storeId: number,
    catalogId: number,
    catalogImportType: CatalogImportType,
  ): Observable<CatalogImport> {
    const formdata = new FormData();
    formdata.append('file', file);

    const importType =
      catalogImportType === CatalogImportType.PRODUCT_EXCEL
        ? 'CREATE'
        : 'UPDATE';

    return this.http.post<CatalogImport>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/product/import-file?importType=${importType}`,
      formdata,
    );
  }

  /**
   * Get pending product import status
   * Retrieves the status of a pending product import operation
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param importId - The import ID to track (null for initial load)
   * @param catalogImportType - The import type (PRODUCT_EXCEL or PRODUCT_UPDATE_EXCEL)
   * @returns Observable of CatalogImport with import status
   */
  getPendingProductImport(
    storeId: number,
    catalogId: number,
    importId: number,
    catalogImportType: CatalogImportType,
  ): Observable<CatalogImport> {
    const importType =
      catalogImportType === CatalogImportType.PRODUCT_EXCEL
        ? 'CREATE'
        : 'UPDATE';

    // If importId is null, fetch any pending import (initial load)
    // Otherwise, fetch specific import by ID (polling)
    if (importId === null || importId === undefined) {
      return this.http.get<CatalogImport>(
        `/api/v1/stores/${storeId}/catalog/${catalogId}/product/import-file?importType=${importType}`,
      );
    }

    return this.http.get<CatalogImport>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/product/import-file/${importId}?importType=${importType}`,
    );
  }

  /**
   * Process product import
   * Triggers the processing of a verified product import
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param importId - The import ID to process
   * @returns Observable of void
   */
  processProductImport(
    storeId: number,
    catalogId: number,
    importId: number,
  ): Observable<void> {
    return this.http.post<void>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/product/import-file/${importId}/process`,
      {},
    );
  }

  /**
   * Cancel product import
   * Cancels a pending product import operation
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param importId - The import ID to cancel
   * @returns Observable of void
   */
  cancelProductImport(
    storeId: number,
    catalogId: number,
    importId: number,
  ): Observable<void> {
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/product/import-file/${importId}`,
    );
  }
}
